import { useCallback, useRef, useState } from 'react';
import { ChevronDown, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { cn } from '@/lib/utils';
import { useUsersApi, useTagsApi, type UserAdminListItem } from '@/services/api';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { CompanyListItem, Tag } from '@ip/shared-types';
import { CompanyAccessCard, type TagFilterableModule } from './CompanyAccessCard';
import { AddCompanyAccess } from './AddCompanyAccess';

interface UserPermissionsDrawerProps {
  open: boolean;
  user: UserAdminListItem;
  /** Companies the acting admin may assign roles/grants in — superadmin: all, company admin: only theirs. */
  companies: CompanyListItem[];
  isSuperAdmin: boolean;
  onClose: () => void;
  /** Parent reloads the user list — this drawer receives a fresh `user` prop as a result. */
  onChanged: () => void;
}

/**
 * Permissions drawer: shows only the companies the user actually has access
 * in (roles or module grants); new companies are added through a searchable
 * picker. All edits auto-save — per-company details live in CompanyAccessCard.
 */
export function UserPermissionsDrawer({
  open,
  user,
  companies,
  isSuperAdmin,
  onClose,
  onChanged,
}: UserPermissionsDrawerProps) {
  const { addRole, removeRole, updateUser } = useUsersApi();
  const { getTags } = useTagsApi();

  const [updatingActive, setUpdatingActive] = useState(false);
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [addingUnassigned, setAddingUnassigned] = useState(false);
  const [removingRoleId, setRemovingRoleId] = useState<string | null>(null);

  // Tags are fetched lazily, only for companies whose card actually renders
  // a tag editor — not for every manageable company up front.
  const [companyTags, setCompanyTags] = useState<
    Record<string, Partial<Record<TagFilterableModule, Tag[]>>>
  >({});
  const tagsRequested = useRef(new Set<string>());
  const ensureTags = useCallback(
    (nip: string) => {
      if (tagsRequested.current.has(nip)) return;
      tagsRequested.current.add(nip);
      Promise.all([getTags(nip, 'expense'), getTags(nip, 'sales')])
        .then(([expense, sales]) =>
          setCompanyTags((prev) => ({ ...prev, [nip]: { expense, sales } })),
        )
        .catch((error) => {
          tagsRequested.current.delete(nip);
          console.error('Failed to load tags for permissions drawer:', error);
        });
    },
    [getTags],
  );

  const assignedNips = new Set<string>([
    ...user.roles.flatMap((r) => (r.companyNip ? [r.companyNip] : [])),
    ...user.grants.map((g) => g.companyNip),
  ]);
  const assignedCompanies = companies.filter((c) => assignedNips.has(c.nip));
  const availableCompanies = companies.filter((c) => !assignedNips.has(c.nip));
  const unassignedRoles = user.roles.filter((r) => r.companyNip === null);

  const handleToggleActive = async (checked: boolean) => {
    try {
      setUpdatingActive(true);
      await updateUser(user.id, { isActive: checked });
      toast.success(t('users.drawer.userUpdated'));
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('users.errors.updateFailed'));
      console.error(error);
    } finally {
      setUpdatingActive(false);
    }
  };

  const handleAddUnassigned = async () => {
    try {
      setAddingUnassigned(true);
      await addRole(user.id, { role: 'pracownik', companyNip: null });
      toast.success(t('users.drawer.roleAdded'));
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('users.errors.addRoleFailed'));
      console.error(error);
    } finally {
      setAddingUnassigned(false);
    }
  };

  const handleRemoveRole = async (roleId: string) => {
    try {
      setRemovingRoleId(roleId);
      await removeRole(user.id, roleId);
      toast.success(t('users.drawer.roleRemoved'));
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('users.errors.removeRoleFailed'));
      console.error(error);
    } finally {
      setRemovingRoleId(null);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{t('users.drawer.title')}</SheetTitle>
          <SheetDescription>{user.displayName || user.email}</SheetDescription>
        </SheetHeader>

        <div className="flex items-center justify-between border-b px-4 pb-3">
          <div>
            <Label className="text-sm">{t('users.drawer.active')}</Label>
            <p className="text-muted-foreground text-xs">{t('users.drawer.activeHint')}</p>
          </div>
          <Switch checked={user.isActive} disabled={updatingActive} onCheckedChange={handleToggleActive} />
        </div>

        {user.authProvider === 'entra' && (
          <div className="border-b">
            <button
              type="button"
              onClick={() => setDirectoryOpen((o) => !o)}
              className="text-muted-foreground hover:text-foreground flex w-full items-center justify-between px-4 py-2 text-xs"
            >
              <span>{t('users.directory.title')}</span>
              <ChevronDown
                className={cn('size-3.5 transition-transform', directoryOpen && 'rotate-180')}
              />
            </button>
            {directoryOpen && (
              <div className="space-y-1.5 px-4 pb-3">
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-muted-foreground">{t('users.directory.jobTitle')}</dt>
                  <dd>{user.jobTitle || '—'}</dd>
                  <dt className="text-muted-foreground">{t('users.directory.department')}</dt>
                  <dd>{user.department || '—'}</dd>
                  <dt className="text-muted-foreground">{t('users.directory.manager')}</dt>
                  <dd>
                    {user.managerEmail || user.managerDisplayName ? (
                      <>
                        {user.managerDisplayName || user.managerEmail}
                        {user.managerDisplayName && user.managerEmail && (
                          <span className="text-muted-foreground ml-1 text-xs">{user.managerEmail}</span>
                        )}
                      </>
                    ) : (
                      t('users.directory.noManager')
                    )}
                  </dd>
                </dl>
                <p className="text-muted-foreground text-xs">
                  {t('users.directory.syncedAt')}:{' '}
                  {user.directorySyncedAt
                    ? new Date(user.directorySyncedAt).toLocaleString()
                    : t('users.directory.neverSynced')}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {assignedCompanies.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {t('users.drawer.noAccess')}
            </p>
          ) : (
            assignedCompanies.map((company) => (
              <CompanyAccessCard
                key={company.nip}
                user={user}
                company={company}
                tags={companyTags[company.nip]}
                onNeedTags={() => ensureTags(company.nip)}
                onChanged={onChanged}
              />
            ))
          )}

          <AddCompanyAccess userId={user.id} companies={availableCompanies} onChanged={onChanged} />

          {isSuperAdmin && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed p-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="text-sm font-medium">{t('users.drawer.unassignedTitle')}</p>
                {unassignedRoles.map((role) => (
                  <Badge key={role.id} variant="secondary" className="gap-1">
                    {t('users.roleNames.pracownik')}
                    <button
                      type="button"
                      aria-label={t('common.delete')}
                      disabled={removingRoleId === role.id}
                      onClick={() => handleRemoveRole(role.id)}
                      className="opacity-70 hover:opacity-100 disabled:opacity-40"
                    >
                      {removingRoleId === role.id ? (
                        <Loader2 className="size-3 animate-spin" />
                      ) : (
                        <X className="size-3" />
                      )}
                    </button>
                  </Badge>
                ))}
              </div>
              {unassignedRoles.length === 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={addingUnassigned}
                  onClick={handleAddUnassigned}
                >
                  {addingUnassigned && <Loader2 className="size-4 animate-spin" />}
                  {t('users.drawer.addUnassigned')}
                </Button>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
